import React, { useState, useEffect } from 'react';
import { Activity, ArrowRight, ArrowLeft, Loader2 } from 'lucide-react';
import Modal from './Modal';
import WidgetConfigurationScript from './WidgetConfigurationScript';
import { tenantApi, ApiKeyCreateRequest, ApiKeyCreateResponse } from '../lib/tenant-api';
import { useTenantAuth } from '../contexts/TenantAuthContext';

interface CreateApiKeyModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (apiKey: ApiKeyCreateResponse) => void;
  defaultIsPreview?: boolean;
}

const CreateApiKeyModal: React.FC<CreateApiKeyModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  defaultIsPreview = false,
}) => {
  const { tenant } = useTenantAuth();
  const [step, setStep] = useState<'create' | 'configure'>('create');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createdApiKey, setCreatedApiKey] = useState<ApiKeyCreateResponse | null>(null);

  const defaultFormData = (): ApiKeyCreateRequest => ({
    name: '',
    description: '',
    allowed_origins: [import.meta.env.VITE_DEFAULT_ALLOWED_ORIGIN || 'https://app-lawma-prod-ai-api-westus2-001-g9cshwd4bvhyh3bj.westus2-01.azurewebsites.net'],
    expires_at: '',
    is_preview: defaultIsPreview,
  });

  // Form state
  const [formData, setFormData] = useState<ApiKeyCreateRequest>(defaultFormData);

  // Sync defaultIsPreview when modal opens
  useEffect(() => {
    if (isOpen) {
      setFormData(defaultFormData());
      setStep('create');
      setError(null);
      setCreatedApiKey(null);
    }
  }, [isOpen, defaultIsPreview]);

  const handleClose = () => {
    setFormData(defaultFormData());
    setStep('create');
    setCreatedApiKey(null);
    setError(null);
    onClose();
  };

  const handleInputChange = (field: keyof ApiKeyCreateRequest, value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleOriginsChange = (value: string) => {
    // Split by comma and clean up
    const origins = value
      .split(',')
      .map(origin => origin.trim())
      .filter(origin => origin.length > 0);

    setFormData(prev => ({
      ...prev,
      allowed_origins: origins,
    }));
  };

  const handleCreateApiKey = async () => {
    if (!formData.name.trim()) {
      setError('API key name is required');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await tenantApi.createApiKey({
        ...formData,
        expires_at: formData.expires_at || undefined,
      });

      setCreatedApiKey(response);

      if (formData.is_preview) {
        window.dispatchEvent(new Event('widget-api-key-changed'));
        onSuccess?.(response);
        handleClose();
        return;
      }

      setStep('configure');
      onSuccess?.(response);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create API key');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToCreate = () => {
    setStep('create');
  };

  const renderCreateStep = () => (
    <div className="space-y-4">
      <div className="flex items-center space-x-2 text-sm text-gray-600 mb-4">
        <div className="flex items-center justify-center w-6 h-6 bg-indigo-100 text-indigo-700 rounded-full text-xs font-semibold">
          1
        </div>
        <span>Create API Key</span>
        <ArrowRight className="h-4 w-4 text-gray-400" />
        <div className="flex items-center justify-center w-6 h-6 bg-gray-100 text-gray-400 rounded-full text-xs font-semibold">
          2
        </div>
        <span className="text-gray-400">Widget Configuration</span>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 ">
          {error}
        </div>
      )}

      <div>
        <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
          API Key Name <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          id="name"
          value={formData.name}
          onChange={(e) => handleInputChange('name', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300  shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          placeholder="e.g., Website Widget Key"
          required
        />
      </div>

      <div>
        <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
          Description
        </label>
        <textarea
          id="description"
          value={formData.description}
          onChange={(e) => handleInputChange('description', e.target.value)}
          rows={3}
          className="w-full px-3 py-2 border border-gray-300  shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          placeholder="Optional description for this API key"
        />
      </div>

      <div>
        <label htmlFor="allowed_origins" className="block text-sm font-medium text-gray-700 mb-1">
          Allowed Origins
        </label>
        <input
          type="text"
          id="allowed_origins"
          value={formData.allowed_origins?.join(', ') || ''}
          onChange={(e) => handleOriginsChange(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300  shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          placeholder="e.g., https://api.lawma.ai, https://www.yoursite.com"
        />
        <p className="mt-1 text-sm text-gray-500">
          Default origin is preset. You can add more origins, separated by commas.
        </p>
      </div>

      <div>
        <label htmlFor="expires_at" className="block text-sm font-medium text-gray-700 mb-1">
          Expiration Date
        </label>
        <input
          type="datetime-local"
          id="expires_at"
          value={formData.expires_at}
          onChange={(e) => handleInputChange('expires_at', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300  shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
        />
        <p className="mt-1 text-sm text-gray-500">
          Leave empty for no expiration.
        </p>
      </div>

      <div className="flex items-center space-x-2">
        <input
          type="checkbox"
          id="is_preview"
          checked={formData.is_preview}
          onChange={(e) => handleInputChange('is_preview', e.target.checked)}
          className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 "
        />
        <label htmlFor="is_preview" className="text-sm font-medium text-gray-700">
          Set as preview key
        </label>
        <div className="ml-1 group relative">
          <span className="text-gray-400 hover:text-gray-500 cursor-help">ⓘ</span>
          <div className="hidden group-hover:block absolute bottom-full left-1/2 transform -translate-x-1/2 w-64 p-2 bg-gray-800 text-white text-xs  shadow-lg mb-2">
            Preview keys allow you to test your integration before going live. Only one preview key can be active at a time.
          </div>
        </div>
      </div>
    </div>
  );

  const renderConfigureStep = () => (
    <div className="space-y-4">
      <div className="flex items-center space-x-2 text-sm text-gray-600 mb-4">
        <div className="flex items-center justify-center w-6 h-6 bg-green-100 text-green-700 rounded-full text-xs font-semibold">
          ✓
        </div>
        <span className="text-green-700">API Key Created</span>
        <ArrowRight className="h-4 w-4 text-gray-400" />
        <div className="flex items-center justify-center w-6 h-6 bg-indigo-100 text-indigo-700 rounded-full text-xs font-semibold">
          2
        </div>
        <span>Widget Configuration</span>
      </div>

      <div className="bg-green-50 border border-green-200  p-4 mb-6">
        <div className="flex items-center">
          <Activity className="h-5 w-5 text-green-600 mr-2" />
          <div>
            <h4 className="text-sm font-semibold text-green-900">API Key Created Successfully!</h4>
            <p className="text-sm text-green-800 mt-1">
              Your API key "<strong>{createdApiKey?.name}</strong>" has been created and is ready to use.
            </p>
          </div>
        </div>
      </div>

      {createdApiKey && tenant && (
        <WidgetConfigurationScript
          apiKey={createdApiKey.api_key}
          tenantName={tenant.name}
          isKeyVisible={true}
        />
      )}
    </div>
  );

  const renderFooter = () => {
    if (step === 'create') {
      return (
        <>
          <button
            type="button"
            onClick={handleClose}
            className="w-full inline-flex justify-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300  shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:w-auto sm:text-sm"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleCreateApiKey}
            disabled={isLoading || !formData.name.trim()}
            className="w-full inline-flex justify-center px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent  shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed sm:w-auto sm:text-sm"
          >
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Creating...
              </>
            ) : (
              'Create API Key'
            )}
          </button>
        </>
      );
    }

    return (
      <>
        <button
          type="button"
          onClick={handleBackToCreate}
          className="w-full inline-flex justify-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300  shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:w-auto sm:text-sm"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </button>
        <button
          type="button"
          onClick={handleClose}
          className="w-full inline-flex justify-center px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent  shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:w-auto sm:text-sm"
        >
          Done
        </button>
      </>
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={step === 'create' ? 'Create New API Key' : 'Widget Configuration'}
      size="md"
      footer={renderFooter()}
    >
      {step === 'create' ? renderCreateStep() : renderConfigureStep()}
    </Modal>
  );
};

export default CreateApiKeyModal;
